import {
  clearSilentReauth,
  getPersistedOrgId,
  markSilentReauthTried,
  setPersistedOrgId,
  silentReauthAlreadyTried,
  useAuth0
} from "@appretio/auth";
import { resolveOrgByName } from "@appretio/shared";
import { ProgressSpinner } from "@appretio/ui-kit";
import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { loadingScreen } from "./AuthGuard.css";
import { LoginScreen } from "./LoginScreen";

// Dev / e2e bypass — pairs with `APPRETIO_AUTH_DISABLED` on the API.
// When `VITE_AUTH_DISABLED=true` is set in the local `.env`, skip the
// Auth0 redirect and render the route tree straight through. Any other
// value (including unset) keeps the production Auth0 flow.
const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

function LoadingScreen() {
  return (
    <div className={loadingScreen}>
      <ProgressSpinner />
    </div>
  );
}

/** Pull the Auth0 error code (e.g. "consent_required") off a rejected token call. */
function authErrorCode(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "error" in error) {
    const code = (error as { error?: unknown }).error;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

/** Return path with Auth0 callback params (?error/?code/?state) stripped. */
function cleanReturnTo(): string {
  const url = new URL(window.location.href);
  for (const param of ["error", "error_description", "code", "state"]) {
    url.searchParams.delete(param);
  }
  return url.pathname + url.search;
}

/**
 * Pull the org hint (`?org=<tenant name>`) off the URL, stripping it so it
 * can't replay through later redirects. Feedback restore links carry it.
 */
function consumeOrgHint(): string | null {
  const url = new URL(window.location.href);
  const hint = url.searchParams.get("org");
  if (hint === null) {
    return null;
  }
  url.searchParams.delete("org");
  window.history.replaceState({}, "", url.pathname + url.search);
  return hint;
}

export function AuthGuard() {
  const { isLoading, error, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();

  // Gate rendering on a *verified* token, not on `isAuthenticated`: a cached
  // id-token can keep `isAuthenticated` true after the session is gone, which
  // would render the app, fire a doomed API call, and bounce to login. Holding
  // the loading screen until a real token resolves avoids that flash entirely.
  const [tokenReady, setTokenReady] = useState(false);

  // First login (no tenant chosen yet) → show the in-app email-discovery screen
  // instead of redirecting org-less, which organization_usage=require can't
  // complete silently.
  const [showLogin, setShowLogin] = useState(false);

  // Renewal is one-shot per page load — it always ends in either a token or a
  // redirect. The ref stops the effect re-firing while the SDK state churns.
  const renewalStarted = useRef(false);

  useEffect(() => {
    if (AUTH_DISABLED) return;
    if (isLoading) return;
    if (renewalStarted.current) return;
    renewalStarted.current = true;

    // The Auth0 callback came back with an error while an org was persisted —
    // that org is unusable for this user (membership revoked, org deleted, or a
    // stale id from a different Auth0 tenant/env: dev and prod share the
    // localhost origin and the same localStorage key, so a persisted org can
    // leak across env switches). Redirecting again would just re-enter the same
    // rejected org and loop. Clear the selection and fall back to email
    // discovery so the user can pick a valid org.
    if (error && getPersistedOrgId()) {
      setPersistedOrgId(null);
      clearSilentReauth();
      window.history.replaceState({}, "", cleanReturnTo());
      setShowLogin(true);
      return;
    }

    // Org-hint handoff: resolve the hinted tenant name and start an org-scoped
    // login for it (silent bounce when an Auth0/IdP session is live). Advisory
    // like all pre-login routing — an unknown/unserved name or a failed lookup
    // just falls back to the normal flow, and a hint matching the already
    // persisted org needs no redirect at all.
    const orgHint = consumeOrgHint();
    if (orgHint) {
      resolveOrgByName(orgHint)
        .then((org) => {
          if (org && org.id !== getPersistedOrgId()) {
            setPersistedOrgId(org.id);
            clearSilentReauth();
            return loginWithRedirect({
              authorizationParams: { organization: org.id },
              appState: { returnTo: cleanReturnTo() }
            });
          }
          renewSession();
        })
        .catch(() => renewSession());
      return;
    }

    renewSession();

    function renewSession() {
      getAccessTokenSilently()
        .then(() => {
          clearSilentReauth(); // a working token proves the session recovered
          setTokenReady(true);
        })
        .catch((error: unknown) => {
          const returnTo = cleanReturnTo();
          // No tenant chosen yet: discover the org from the user's email in-app
          // before redirecting — we can't start an org-scoped login without one.
          if (!getPersistedOrgId()) {
            setShowLogin(true);
            return;
          }
          const code = authErrorCode(error);
          const needsInteractive =
            code === "consent_required" || code === "interaction_required";
          // Auth0 never skips the consent screen for localhost callback URLs, so
          // the silent prompt=none hop always fails there with consent_required.
          const isLocalhost =
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";

          // consent_required / interaction_required need a UI a silent hop can't
          // satisfy; a prior failed silent attempt means we're out of silent
          // options; localhost is always futile. In every case go interactive.
          if (needsInteractive || silentReauthAlreadyTried() || isLocalhost) {
            clearSilentReauth();
            loginWithRedirect({ appState: { returnTo } });
            return;
          }

          // Otherwise (e.g. login_required): one full-page prompt=none redirect to
          // pick up the first-party Auth0 session cookie. The sessionStorage guard
          // survives the redirect so the return trip escalates instead of looping.
          markSilentReauthTried();
          loginWithRedirect({
            authorizationParams: { prompt: "none" },
            appState: { returnTo }
          });
        });
    }
  }, [isLoading, error, getAccessTokenSilently, loginWithRedirect]);

  if (AUTH_DISABLED) {
    return <Outlet />;
  }

  if (showLogin) {
    return <LoginScreen />;
  }

  if (!tokenReady) {
    return <LoadingScreen />;
  }

  return <Outlet />;
}
